import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useExtraWorkOrdersAgGridColumns, useExtraWorkOrdersMain } from '../../hooks';
import { CardList, ExtraWorkOrdersAgGridTable, ExtraWorkOrdersBreadcrumb } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection } from '../interaction';

/**
 * Componente principal del listado de órdenes de trabajos extra
 */
export const Main = () => {

  const {
    orders,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
    sorting,
    selectedColumns,
    ordersViewProps,
    exportColumns,
    hasActiveFilters,
    fixedColumns,
    filterConditions,
    searchTerm,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleSortingChange,
  } = useExtraWorkOrdersMain();

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const { filteredColumnDefs, tanStackColumns } = useExtraWorkOrdersAgGridColumns({
    selectedColumns,
  });

  const selectorColumns = useMemo(() => tanStackColumns, [tanStackColumns]);

  const renderOrders = () => {
    if (isMobile) {
      return (
        <CardList
          {...ordersViewProps}
          sorting={sorting}
          onSortingChange={handleSortingChange}
        />
      );
    }

    return (
      <ExtraWorkOrdersAgGridTable
        orders={ordersViewProps.orders}
        columnDefs={filteredColumnDefs}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isFetching={isFetching}
        sorting={sorting}
        onSortingChange={handleSortingChange}
      />
    );
  };

  if (isInitialLoad) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <>
      <ExtraWorkOrdersBreadcrumb />
      <div className="px-6 pt-4">
        <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              handleSearch={handleSearch}
              onFilterChange={handleFilterChange}
              allColumns={selectorColumns}
              selectedColumns={selectedColumns}
              onColumnsChange={handleColumnsChange}
              fixedColumns={fixedColumns}
              exportColumns={exportColumns}
              searchTerm={searchTerm}
              filterConditions={filterConditions}
            />
          </div>
          <div>
            <TotalResults total={total} />
          </div>
        </div>
      </div>

      {/* 🎯 SOLUCIÓN: Siempre renderizar AgGrid, pero con opacity 0 cuando no hay datos */}
      {!isInitialLoad && (
        <div className="relative mb-4 mt-4">
          {/* AgGrid siempre renderizado, con opacity 0 si no hay datos */}
          <div
            className={`transition-opacity duration-300 ${
              isFetching && !isFetchingNextPage
                ? 'opacity-50 pointer-events-none'
                : total > 0
                  ? 'opacity-100'
                  : 'opacity-0'
            }`}
          >
            {renderOrders()}
          </div>

          {/* EmptyView solo cuando no hay datos */}
          {total === 0 && (
            <div className="absolute inset-0">
              <EmptyView
                type={hasActiveFilters ? 'noResults' : 'emptyState'}
                icon={EMPTY_VIEW_ICONS.ORDERS}
              />
            </div>
          )}
        </div>
      )}
      </div>
    </>
  );
};
